import React from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import {
    LayoutDashboard, Package, Boxes, CalendarClock, ShoppingCart, Truck, Factory,
    Users as UsersIcon, ShieldCheck, ScrollText, Settings as SettingsIcon, LogOut, FileText, Menu, X,
    UserCircle, Repeat, Ticket, Store, Contact, Zap, BarChart3, Stethoscope,
    LayoutTemplate, Newspaper, FlaskConical, Search, Star,
} from "lucide-react";
import { useRhmAuth } from "./RhmAuthContext";
import Logo from "../components/Logo";

const NAV = [
    {
        group: "Overview",
        items: [{ to: "/rhm", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard.view", end: true }],
    },
    {
        group: "Catalogue",
        items: [{ to: "/rhm/products", label: "Products", icon: Package, perm: "products.view" }],
    },
    {
        group: "Inventory",
        items: [
            { to: "/rhm/inventory", label: "Stock", icon: Boxes, perm: "inventory.view" },
            { to: "/rhm/batches", label: "Batches & Expiry", icon: CalendarClock, perm: "batches.view" },
        ],
    },
    {
        group: "Procurement",
        items: [
            { to: "/rhm/purchase-orders", label: "Purchase Orders", icon: Truck, perm: "purchases.view" },
            { to: "/rhm/suppliers", label: "Suppliers", icon: Factory, perm: "suppliers.view" },
        ],
    },
    {
        group: "Commerce",
        items: [
            { to: "/rhm/orders", label: "Orders", icon: ShoppingCart, perm: "orders.view" },
            { to: "/rhm/offers", label: "Offers & Discounts", icon: Ticket, perm: "offers.view" },
        ],
    },
    {
        group: "CRM",
        items: [
            { to: "/rhm/bookings", label: "Consultation Desk", icon: CalendarClock, perm: "consultations.view" },
            { to: "/rhm/consultations", label: "Consultation Setup", icon: Stethoscope, perm: "consultations.view" },
            { to: "/rhm/customers", label: "Customers", icon: UserCircle, perm: "customers.view" },
            { to: "/rhm/reorders", label: "Reorders", icon: Repeat, perm: "customers.view" },
            { to: "/rhm/dealers", label: "Dealers", icon: Store, perm: "dealers.view" },
            { to: "/rhm/leads", label: "Leads & Follow-ups", icon: Contact, perm: "leads.view" },
        ],
    },
    {
        group: "Growth",
        items: [
            { to: "/rhm/website", label: "Website Layout", icon: LayoutTemplate, perm: "website.view" },
            { to: "/rhm/dashboard-layout", label: "Dashboard Layout", icon: LayoutTemplate, perm: "website.view" },
            { to: "/rhm/science", label: "The Science", icon: FlaskConical, perm: "website.view" },
            { to: "/rhm/blog", label: "Blog", icon: Newspaper, perm: "blog.view" },
            { to: "/rhm/seo", label: "SEO & Google", icon: Search, perm: "website.view" },
            { to: "/rhm/reviews", label: "Customer Reviews", icon: Star, perm: "testimonials.view" },
            { to: "/rhm/automation", label: "Automation", icon: Zap, perm: "automation.view" },
            { to: "/rhm/reports", label: "Reports", icon: BarChart3, perm: "reports.view" },
        ],
    },
    {
        group: "Administration",
        items: [
            { to: "/rhm/users", label: "Users", icon: UsersIcon, perm: "users.view" },
            { to: "/rhm/roles", label: "Roles & Permissions", icon: ShieldCheck, perm: "roles.view" },
            { to: "/rhm/audit", label: "Audit Log", icon: ScrollText, perm: "audit.view" },
            { to: "/rhm/settings", label: "Settings", icon: SettingsIcon, perm: "settings.view" },
            { to: "/admin", label: "Website Content", icon: FileText, perm: "content.view" },
        ],
    },
];

export default function RhmLayout() {
    const { user, loading, can, logout } = useRhmAuth();
    const [open, setOpen] = React.useState(false);
    const loc = useLocation();

    React.useEffect(() => { setOpen(false); }, [loc.pathname]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center mono text-xs text-zinc-500">LOADING RHM…</div>;
    }
    if (!user) return <Navigate to="/rhm/login" replace state={{ from: loc.pathname }} />;

    const groups = NAV.map((g) => ({ ...g, items: g.items.filter((i) => can(i.perm)) })).filter((g) => g.items.length);

    return (
        <div className="min-h-screen bg-bg0 flex">
            <aside
                className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-[264px] shrink-0 bg-bg1 border-r border-white/10 flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
                data-testid="rhm-sidebar"
            >
                <div className="px-5 h-16 flex items-center gap-3 border-b border-white/10">
                    <Logo className="h-6 w-auto max-w-[110px] shrink-0" alt="RHM" forceVariant="dark" />
                    <div className="pl-3 ml-1 border-l border-white/10 shrink-0">
                        <div className="font-display text-sm leading-none text-gold">CRM</div>
                        <div className="mono text-[8px] tracking-[0.2em] text-zinc-500 mt-1">PLATFORM</div>
                    </div>
                </div>

                <nav className="flex-1 overflow-y-auto py-4 no-scrollbar">
                    {groups.map((g) => (
                        <div key={g.group} className="mb-5">
                            <div className="px-5 mono text-[9px] tracking-[0.22em] text-zinc-600 mb-2">{g.group}</div>
                            {g.items.map((i) => (
                                <NavLink
                                    key={i.to}
                                    to={i.to}
                                    end={i.end}
                                    data-testid={`rhm-nav-${i.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors ${
                                            isActive
                                                ? "border-gold text-gold bg-gold/5"
                                                : "border-transparent text-zinc-400 hover:text-white hover:bg-white/5"
                                        }`
                                    }
                                >
                                    <i.icon className="w-4 h-4 shrink-0" />
                                    <span className="truncate">{i.label}</span>
                                </NavLink>
                            ))}
                        </div>
                    ))}
                </nav>

                <div className="border-t border-white/10 p-4">
                    <div className="text-sm font-semibold truncate" data-testid="rhm-current-user">{user.name}</div>
                    <div className="mono text-[9px] tracking-[0.16em] text-gold mt-1">
                        {user.role?.name}{user.department ? ` · ${user.department}` : ""}
                    </div>
                    <button
                        onClick={logout}
                        data-testid="rhm-logout"
                        className="mt-3 w-full inline-flex items-center justify-center gap-2 mono text-[10px] tracking-[0.16em] border border-white/15 py-2 hover:border-gold hover:text-gold transition-colors"
                    >
                        <LogOut className="w-3.5 h-3.5" /> SIGN OUT
                    </button>
                    <Link to="/" className="block mt-2 text-center mono text-[9px] tracking-[0.16em] text-zinc-500 hover:text-gold">
                        VIEW WEBSITE
                    </Link>
                </div>
            </aside>

            {open && <div className="fixed inset-0 z-40 bg-black/70 lg:hidden" onClick={() => setOpen(false)} />}

            <div className="flex-1 min-w-0">
                <header className="lg:hidden sticky top-0 z-30 h-16 px-5 flex items-center justify-between bg-bg0/90 backdrop-blur border-b border-white/10">
                    <div className="font-display text-lg">RHM <span className="text-gold">CRM</span></div>
                    <button onClick={() => setOpen((v) => !v)} data-testid="rhm-mobile-menu" className="p-2 border border-white/15">
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </header>
                <main className="p-5 md:p-8 max-w-[1500px]">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
